import { useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { EntityPathOption } from '../types';
import { useAuth } from '../context/AuthContext';

// Plan 83: read-only fetch of allowed entity_path values for the device-edit
// dropdown. Server gates this endpoint with write_devices, so non-edit users
// shouldn't call it.
//
// FS-890: the API answers with the options inside the caller's scopes, so the
// list belongs to the signed-in account and its scopes. AuthContext can swap
// either without a remount: the list held for the previous ones is dropped at
// once (it is never returned under another authority), read again, and an
// answer that arrives for the old ones is ignored. A failed read is `error`,
// apart from a list that is merely empty.
export function useEntityPathOptions(enabled: boolean = true) {
  const { id, scopes } = useAuth();
  // Who the list is for; null while disabled.
  const authority = enabled ? `${id}\n${JSON.stringify(scopes ?? [])}` : null;
  const [result, setResult] = useState<{
    authority: string;
    options: EntityPathOption[];
    error: string | null;
  } | null>(null);

  useEffect(() => {
    if (authority === null) {
      setResult(null);
      return;
    }
    let cancelled = false;
    axiosInstance
      .get<EntityPathOption[]>(`${config.devicesApiUrl}/devices/entity-path-options`)
      .then((res) => {
        if (!cancelled) setResult({ authority, options: res.data, error: null });
      })
      .catch((err) => {
        console.error('Error fetching entity path options:', err);
        if (!cancelled) {
          setResult({
            authority,
            options: [],
            error: 'Could not load the configured paths; Custom… still takes a typed path.',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [authority]);

  const current = authority !== null && result?.authority === authority ? result : null;
  return {
    options: current?.options ?? [],
    loading: authority !== null && current === null,
    error: current?.error ?? null,
  };
}
